import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../firebase';
import { calculateEffectiveStatus } from '../utils/statusUtils';

export const SEED_PROTOCOLS = [
  // Supplements
  {
    name: "Vitamin D3 + K2",
    category: "Supplement",
    primaryCategory: "Immune Support",
    secondaryCategory: "Hormone Support",
    dosageMin: 125,
    dosageMax: null,
    unit: "mcg",
    administrationFormat: "Oral",
    timingOfDay: "AM",
    frequencySchedule: "Daily",
    brandOrSupplier: "Generic",
    intendedOutcome: "Bone health, Immune support, Calcium regulation",
    notes: "125 mcg (5000 IU) D3 / 100 mcg K2",
    cycleStatus: "Active",
    startDate: new Date().toISOString().split('T')[0],
    endDate: null
  },
  {
    name: "TMG",
    category: "Supplement",
    primaryCategory: "Longevity & Cellular Health",
    secondaryCategory: "Cardiovascular Health",
    dosageMin: 500,
    dosageMax: 1000,
    unit: "mg",
    administrationFormat: "Oral",
    timingOfDay: "AM",
    frequencySchedule: "Daily",
    brandOrSupplier: "Generic",
    intendedOutcome: "Methylation support, Homocysteine balance",
    notes: "Paired with NMN administration",
    cycleStatus: "Active",
    startDate: new Date().toISOString().split('T')[0],
    endDate: null
  },
  {
    name: "NMN",
    category: "Supplement",
    primaryCategory: "Longevity & Cellular Health",
    secondaryCategory: "Cognitive, Energy & Focus",
    dosageMin: 500,
    dosageMax: 1000,
    unit: "mg",
    administrationFormat: "Oral",
    timingOfDay: "AM",
    frequencySchedule: "Daily",
    brandOrSupplier: "Generic",
    intendedOutcome: "NAD+ booster, Cellular energy, Longevity",
    notes: "Take fasted in morning",
    cycleStatus: "Active",
    startDate: new Date().toISOString().split('T')[0],
    endDate: null
  },
  {
    name: "Tadalafil",
    category: "Supplement",
    primaryCategory: "Cardiovascular Health",
    secondaryCategory: "Strength & Recovery",
    dosageMin: 2.5,
    dosageMax: 5,
    unit: "mg",
    administrationFormat: "Oral",
    timingOfDay: "AM",
    frequencySchedule: "Daily",
    brandOrSupplier: "Generic",
    intendedOutcome: "Vasodilation, Endothelial function, Pump",
    notes: "Daily low dose regimen",
    cycleStatus: "Active",
    startDate: new Date().toISOString().split('T')[0],
    endDate: null
  },
  {
    name: "Algae Oil",
    category: "Supplement",
    primaryCategory: "Cardiovascular Health",
    secondaryCategory: "Cognitive, Energy & Focus",
    dosageMin: 585,
    dosageMax: null,
    unit: "mg",
    administrationFormat: "Oral",
    timingOfDay: "AM",
    frequencySchedule: "Daily",
    brandOrSupplier: "Generic",
    intendedOutcome: "Omega-3 fatty acids, Cardiovascular & Brain health",
    notes: "195 mg EPA / 390 mg DHA",
    cycleStatus: "Active",
    startDate: new Date().toISOString().split('T')[0],
    endDate: null
  },
  {
    name: "Astaxanthin",
    category: "Supplement",
    primaryCategory: "Skin, Hair & Eye Health",
    secondaryCategory: "Longevity & Cellular Health",
    dosageMin: 12,
    dosageMax: null,
    unit: "mg",
    administrationFormat: "Oral",
    timingOfDay: "AM",
    frequencySchedule: "Daily",
    brandOrSupplier: "Isprelon",
    intendedOutcome: "Potent antioxidant, Skin elasticity, Eye health",
    notes: "Lipid soluble, take with fat source",
    cycleStatus: "Active",
    startDate: new Date().toISOString().split('T')[0],
    endDate: null
  },
  {
    name: "Magnesium Glycinate",
    category: "Supplement",
    primaryCategory: "Sleep & Circadian",
    secondaryCategory: "Strength & Recovery",
    dosageMin: 200,
    dosageMax: null,
    unit: "mg elemental",
    administrationFormat: "Oral",
    timingOfDay: "Bedtime",
    frequencySchedule: "Daily",
    brandOrSupplier: "Generic",
    intendedOutcome: "Sleep quality, Muscle relaxation, Recovery",
    notes: "200 mg elemental magnesium",
    cycleStatus: "Active",
    startDate: new Date().toISOString().split('T')[0],
    endDate: null
  },
  {
    name: "L-Theanine",
    category: "Supplement",
    primaryCategory: "Mood & Stress",
    secondaryCategory: "Cognitive, Energy & Focus",
    dosageMin: 25,
    dosageMax: null,
    unit: "mg",
    administrationFormat: "Oral",
    timingOfDay: "AM / Evening",
    frequencySchedule: "Daily",
    brandOrSupplier: "Generic",
    intendedOutcome: "Calm focus, GABA support",
    notes: "Smooths out caffeine curve",
    cycleStatus: "Active",
    startDate: new Date().toISOString().split('T')[0],
    endDate: null
  },
  {
    name: "Melatonin",
    category: "Supplement",
    primaryCategory: "Sleep & Circadian",
    secondaryCategory: "General or Other Health",
    dosageMin: 2.5,
    dosageMax: null,
    unit: "mg",
    administrationFormat: "Oral",
    timingOfDay: "Bedtime",
    frequencySchedule: "Daily",
    brandOrSupplier: "Generic",
    intendedOutcome: "Circadian rhythm alignment, Antioxidant",
    notes: "Take 30 mins before sleep",
    cycleStatus: "Active",
    startDate: new Date().toISOString().split('T')[0],
    endDate: null
  },

  // Peptides
  {
    name: "Tirzepatide",
    category: "Peptide",
    primaryCategory: "Metabolic Health",
    secondaryCategory: null,
    dosageMin: 4,
    dosageMax: null,
    unit: "mg",
    administrationFormat: "Subcutaneous Injection",
    timingOfDay: "AM",
    frequencySchedule: "Weekly (Tuesday AM)",
    brandOrSupplier: "Research Grade",
    intendedOutcome: "GLP-1/GIP dual agonist, Metabolic health, Appetite regulation",
    notes: "SubQ injection every Tuesday morning",
    cycleStatus: "Active",
    startDate: new Date(Date.now() - 56 * 86400000).toISOString().split('T')[0],
    endDate: null
  },
  {
    name: "Cagrilintide",
    category: "Peptide",
    primaryCategory: "Metabolic Health",
    secondaryCategory: null,
    dosageMin: 500,
    dosageMax: null,
    unit: "mcg",
    administrationFormat: "Subcutaneous Injection",
    timingOfDay: "AM",
    frequencySchedule: "Weekly (Saturday AM)",
    brandOrSupplier: "Research Grade",
    intendedOutcome: "Long-acting Amylin analogue, Synergistic metabolic support",
    notes: "SubQ injection every Saturday morning",
    cycleStatus: "Active",
    startDate: new Date(Date.now() - 42 * 86400000).toISOString().split('T')[0],
    endDate: null
  },
  {
    name: "GHK-Cu",
    category: "Peptide",
    primaryCategory: "Skin, Hair & Eye Health",
    secondaryCategory: "Strength & Recovery",
    dosageMin: 2,
    dosageMax: null,
    unit: "mg",
    administrationFormat: "Subcutaneous Injection",
    timingOfDay: "Bedtime",
    frequencySchedule: "Daily",
    brandOrSupplier: "Research Grade",
    intendedOutcome: "Copper peptide, Tissue repair, Collagen synthesis, Anti-inflammatory",
    notes: "Daily SubQ injection before sleep",
    cycleStatus: "Active",
    startDate: new Date().toISOString().split('T')[0],
    endDate: null
  },
  {
    name: "CJC-1295 (no DAC) / Ipamorelin",
    category: "Peptide",
    primaryCategory: "Strength & Recovery",
    secondaryCategory: "Hormone Support",
    dosageMin: 150,
    dosageMax: null,
    unit: "mcg/ea",
    administrationFormat: "Subcutaneous Injection",
    timingOfDay: "Twice Daily (AM & Bedtime Fasted)",
    frequencySchedule: "Daily",
    brandOrSupplier: "Research Grade",
    intendedOutcome: "GH Secretagogue, Recovery, Muscle preservation, Fat oxidation",
    notes: "150 mcg of each compound per dose; administer on empty stomach",
    cycleStatus: "Active",
    startDate: new Date().toISOString().split('T')[0],
    endDate: null
  },

  // Hormones & AAS
  {
    name: "Testosterone Enanthate",
    category: "Hormones & AAS",
    primaryCategory: "Hormone Support",
    secondaryCategory: "Strength & Recovery",
    dosageMin: 100,
    dosageMax: null,
    weeklyTotalDosage: 100,
    unit: "mg",
    administrationFormat: "Subcutaneous Injection",
    timingOfDay: "AM",
    frequencySchedule: "Twice Weekly (Mon / Thu)",
    brandOrSupplier: "Pharma / TRT",
    intendedOutcome: "Hormone Replacement Therapy (TRT), Vitality, Muscle preservation",
    notes: "Split weekly TRT dose into 50 mg injections Mon & Thu AM",
    cycleStatus: "Active",
    startDate: new Date().toISOString().split('T')[0],
    endDate: null
  },
  {
    name: "DHEA",
    category: "Hormones & AAS",
    primaryCategory: "Hormone Support",
    secondaryCategory: "Mood & Stress",
    dosageMin: 25,
    dosageMax: 50,
    weeklyTotalDosage: null,
    unit: "mg",
    administrationFormat: "Oral",
    timingOfDay: "AM",
    frequencySchedule: "Daily",
    brandOrSupplier: "Generic",
    intendedOutcome: "Adrenal support, Neurosteroid precursor balance",
    notes: "Take morning with breakfast",
    cycleStatus: "Active",
    startDate: new Date().toISOString().split('T')[0],
    endDate: null
  }
];

// Helper for Demo / LocalStorage fallback (namespaced per userId)
const BASE_STORAGE_KEY_PROTOCOLS = 'supps_peps_protocols_v1';
const BASE_STORAGE_KEY_AUDIT = 'supps_peps_audit_v1';
const BASE_STORAGE_KEY_CHECKPOINTS = 'supps_peps_checkpoints_v1';

export function getUserStorageKey(baseKey, userId) {
  const safeId = (userId || 'demo').replace(/[^a-zA-Z0-9_-]/g, '_');
  return `${baseKey}_${safeId}`;
}

/**
 * Remove duplicate items by `id`, preserving first-seen order.
 */
function dedupeById(items = []) {
  const seen = new Set();
  return (Array.isArray(items) ? items : []).filter(item => {
    if (!item?.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

/**
 * Generic namespaced localStorage read with legacy un-namespaced migration.
 * @param {string} baseKey
 * @param {string} userId
 * @param {{ migrateLegacy?: boolean, dedupe?: boolean }} opts
 */
function getNamespaced(baseKey, userId, { migrateLegacy = true, dedupe = false } = {}) {
  const namespacedKey = getUserStorageKey(baseKey, userId);
  let data = localStorage.getItem(namespacedKey);
  // Backwards compatibility migration for legacy un-namespaced key
  if (!data && migrateLegacy && (!userId || userId === 'demo_user_123')) {
    data = localStorage.getItem(baseKey);
    if (data) {
      localStorage.setItem(namespacedKey, data);
    }
  }
  const parsed = data ? JSON.parse(data) : [];
  return dedupe ? dedupeById(parsed) : parsed;
}

/**
 * Generic namespaced localStorage write. Always notifies local-mode
 * subscribers via the `local-storage-sync` event (this fixes the latent
 * bug where audit-log writes never dispatched the event).
 */
function saveNamespaced(baseKey, userId, value, { sync = true } = {}) {
  const namespacedKey = getUserStorageKey(baseKey, userId);
  localStorage.setItem(namespacedKey, JSON.stringify(value));
  if (sync && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('local-storage-sync', { detail: { key: namespacedKey } }));
  }
}

function getLocalProtocols(userId) {
  return getNamespaced(BASE_STORAGE_KEY_PROTOCOLS, userId, { dedupe: true });
}

function saveLocalProtocols(userId, items) {
  saveNamespaced(BASE_STORAGE_KEY_PROTOCOLS, userId, items);
}

export function getLocalAuditLogs(userId) {
  return getNamespaced(BASE_STORAGE_KEY_AUDIT, userId);
}

function saveLocalAuditLogs(userId, logs) {
  saveNamespaced(BASE_STORAGE_KEY_AUDIT, userId, logs);
}

function getLocalCheckpoints(userId) {
  return getNamespaced(BASE_STORAGE_KEY_CHECKPOINTS, userId, { dedupe: true });
}

function saveLocalCheckpoints(userId, items) {
  saveNamespaced(BASE_STORAGE_KEY_CHECKPOINTS, userId, items);
}

function isLocalMode(userId) {
  return !isFirebaseConfigured || userId === 'demo_user_123';
}

// Helper to evaluate and auto-transition protocols based on start and end dates
export async function processAutoStatusTransitions(userId, rawProtocols) {
  const today = new Date().toISOString().split('T')[0];
  const processed = [];

  for (const protocol of rawProtocols) {
    const effectiveStatus = calculateEffectiveStatus(protocol, today);

    if (effectiveStatus !== protocol.cycleStatus) {
      const oldStatus = protocol.cycleStatus;
      const updated = {
        ...protocol,
        cycleStatus: effectiveStatus,
        updatedAt: new Date().toISOString()
      };
      
      // Update in storage / Firestore
      if (isLocalMode(userId)) {
        const local = getLocalProtocols(userId);
        const idx = local.findIndex(p => p.id === protocol.id);
        if (idx !== -1) {
          local[idx] = updated;
          saveLocalProtocols(userId, local);
        }
      } else {
        try {
          const docRef = doc(db, 'protocols', protocol.id);
          await updateDoc(docRef, {
            cycleStatus: effectiveStatus,
            updatedAt: serverTimestamp()
          });
        } catch (e) {
          throw new Error(`Failed to sync auto-status transition for ${protocol.name}: ${e.message}`);
        }
      }

      // Determine appropriate audit message
      let logNotes = `Auto-transitioned status from ${oldStatus} to ${effectiveStatus}.`;
      if (effectiveStatus === 'Pending') {
        logNotes = `Auto-set status to Pending: Scheduled start date (${protocol.startDate}) is in the future.`;
      } else if (effectiveStatus === 'Completed') {
        logNotes = `Auto-completed protocol: Scheduled end date (${protocol.endDate}) has concluded.`;
      } else if (oldStatus === 'Pending' && effectiveStatus === 'Active') {
        logNotes = `Protocol activated: Scheduled start date (${protocol.startDate}) has arrived.`;
      }

      // Log Audit record
      await createAuditLog(
        userId, 
        protocol.id, 
        "STATUS_CHANGE", 
        { cycleStatus: { old: oldStatus, new: effectiveStatus } }, 
        logNotes
      );

      processed.push(updated);
    } else {
      processed.push(protocol);
    }
  }

  return processed;
}

// Real-Time Subscriptions (Multi-Device & Tab Synchronization)

/**
 * Local-mode subscription: emits the current namespaced value immediately,
 * then re-emits on cross-tab `storage` events and same-tab
 * `local-storage-sync` dispatches.
 */
function subscribeLocalCollection(baseKey, userId, readLocal, onUpdate) {
  const key = getUserStorageKey(baseKey, userId);
  onUpdate(readLocal(userId));

  const handleStorage = (e) => {
    if (e.key === key || e.detail?.key === key) {
      onUpdate(readLocal(userId));
    }
  };
  window.addEventListener('storage', handleStorage);
  window.addEventListener('local-storage-sync', handleStorage);
  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener('local-storage-sync', handleStorage);
  };
}

function subscribeFirestoreCollection(userId, collectionName, onUpdate, onError, { logLabel, logWarn = false, mapFn, sortFn }) {
  const q = query(
    collection(db, collectionName),
    where('userId', '==', userId)
  );

  return onSnapshot(
    q,
    (querySnapshot) => {
      const items = [];
      const seenIds = new Set();
      querySnapshot.forEach((docSnap) => {
        if (!seenIds.has(docSnap.id)) {
          seenIds.add(docSnap.id);
          items.push(mapFn(docSnap.id, docSnap.data()));
        }
      });
      if (sortFn) items.sort(sortFn);
      onUpdate(items);
    },
    (error) => {
      const logger = logWarn ? console.warn : console.error;
      logger(`Firestore onSnapshot error (${logLabel}):`, error);
      if (onError) onError(error);
    }
  );
}

/**
 * Generic collection subscription covering both local/demo mode and
 * Firestore. Per-collection behavior is configured via:
 * - baseKey / readLocal: localStorage source for local mode
 * - collectionName: Firestore collection for authenticated mode
 * - mapFn(id, data): snapshot doc mapping (timestamp normalization, etc.)
 * - sortFn: post-read ordering
 */
function subscribeCollection(userId, { baseKey, readLocal, collectionName, logLabel, logWarn, mapFn, sortFn }, onUpdate, onError) {
  if (isLocalMode(userId)) {
    return subscribeLocalCollection(baseKey, userId, readLocal, onUpdate);
  }
  return subscribeFirestoreCollection(userId, collectionName, onUpdate, onError, { logLabel, logWarn, mapFn, sortFn });
}

const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '');
const sortByTimestampDesc = (a, b) => new Date(b.timestamp) - new Date(a.timestamp);

function normalizeCheckpointDoc(id, data) {
  const ts = data.createdAt?.toDate
    ? data.createdAt.toDate().toISOString()
    : (data.timestamp || new Date().toISOString());
  return { id, ...data, timestamp: ts };
}

function normalizeAuditDoc(id, data) {
  const ts = data.timestamp?.toDate
    ? data.timestamp.toDate().toISOString()
    : (data.timestamp || new Date().toISOString());
  return { id, ...data, timestamp: ts };
}

export function subscribeToProtocols(userId, onUpdate, onError) {
  return subscribeCollection(userId, {
    baseKey: BASE_STORAGE_KEY_PROTOCOLS,
    readLocal: getLocalProtocols,
    collectionName: 'protocols',
    logLabel: 'protocols',
    mapFn: (id, data) => ({ id, ...data }),
    sortFn: sortByName,
  }, onUpdate, onError);
}

export function subscribeToCheckpoints(userId, onUpdate, onError) {
  return subscribeCollection(userId, {
    baseKey: BASE_STORAGE_KEY_CHECKPOINTS,
    readLocal: getLocalCheckpoints,
    collectionName: 'protocol_checkpoints',
    logLabel: 'checkpoints',
    mapFn: normalizeCheckpointDoc,
    sortFn: sortByTimestampDesc,
  }, onUpdate, onError);
}

export function subscribeToAuditLogs(userId, onUpdate, onError) {
  return subscribeCollection(userId, {
    baseKey: BASE_STORAGE_KEY_AUDIT,
    readLocal: getLocalAuditLogs,
    collectionName: 'audit_logs',
    logLabel: 'audit_logs',
    logWarn: true,
    mapFn: normalizeAuditDoc,
    sortFn: sortByTimestampDesc,
  }, onUpdate, onError);
}

// Service Functions
export async function getProtocols(userId) {
  if (isLocalMode(userId)) {
    const protocols = getLocalProtocols(userId);
    const updatedProtocols = await processAutoStatusTransitions(userId, protocols);
    saveLocalProtocols(userId, updatedProtocols);
    return updatedProtocols;
  }

  try {
    const q = query(
      collection(db, 'protocols'), 
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const protocols = [];
    querySnapshot.forEach((doc) => {
      protocols.push({ id: doc.id, ...doc.data() });
    });

    // Process auto-status transitions for any items whose dates trigger status changes
    const updatedProtocols = await processAutoStatusTransitions(userId, protocols);
    return updatedProtocols;
  } catch (error) {
    throw new Error(`Failed to load protocols: ${error.message}`);
  }
}

export async function addProtocol(userId, protocolData) {
  const now = new Date().toISOString();
  const effectiveStatus = calculateEffectiveStatus(protocolData);
  const payload = {
    ...protocolData,
    cycleStatus: effectiveStatus,
    userId,
    createdAt: now,
    updatedAt: now
  };

  if (isLocalMode(userId)) {
    const createdId = 'p_' + Date.now();
    const newItem = { id: createdId, ...payload };
    const local = getLocalProtocols(userId);
    local.push(newItem);
    saveLocalProtocols(userId, local);
    await createAuditLog(userId, createdId, "CREATE", {}, "Initial creation of protocol item.");
    return newItem;
  }

  try {
    const docRef = await addDoc(collection(db, 'protocols'), {
      ...payload,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    await createAuditLog(userId, docRef.id, "CREATE", {}, "Initial creation of protocol item.");
    return { id: docRef.id, ...payload };
  } catch (e) {
    throw new Error(`Failed to add protocol: ${e.message}`);
  }
}

export async function updateProtocol(userId, protocolId, oldProtocol, updatedData, changeNotes = "") {
  const now = new Date().toISOString();
  
  // Calculate effective status if dates or status are included in the update
  const merged = { ...oldProtocol, ...updatedData };
  const effectiveStatus = calculateEffectiveStatus(merged);
  const dataToApply = {
    ...updatedData,
    cycleStatus: effectiveStatus
  };

  // Compute changed fields
  const changedFields = {};
  Object.keys(dataToApply).forEach((key) => {
    if (JSON.stringify(oldProtocol[key]) !== JSON.stringify(dataToApply[key])) {
      changedFields[key] = {
        old: oldProtocol[key] !== undefined ? oldProtocol[key] : null,
        new: dataToApply[key]
      };
    }
  });

  const isStatusChangeOnly = Object.keys(changedFields).length === 1 && changedFields.cycleStatus;
  const actionType = isStatusChangeOnly ? "STATUS_CHANGE" : "UPDATE";

  if (isLocalMode(userId)) {
    const local = getLocalProtocols(userId);
    const idx = local.findIndex(p => p.id === protocolId);
    if (idx !== -1) {
      local[idx] = { ...local[idx], ...dataToApply, updatedAt: now };
      saveLocalProtocols(userId, local);
    }
    if (Object.keys(changedFields).length > 0) {
      await createAuditLog(userId, protocolId, actionType, changedFields, changeNotes);
    }
    return { id: protocolId, ...oldProtocol, ...dataToApply, updatedAt: now };
  }

  try {
    const docRef = doc(db, 'protocols', protocolId);
    await updateDoc(docRef, {
      ...dataToApply,
      updatedAt: serverTimestamp()
    });
    if (Object.keys(changedFields).length > 0) {
      await createAuditLog(userId, protocolId, actionType, changedFields, changeNotes);
    }
    return { id: protocolId, ...oldProtocol, ...dataToApply, updatedAt: now };
  } catch (e) {
    throw new Error(`Failed to update protocol: ${e.message}`);
  }
}

export async function deleteProtocol(userId, protocolId, oldProtocol) {
  if (isLocalMode(userId)) {
    const local = getLocalProtocols(userId);
    const filtered = local.filter(p => p.id !== protocolId);
    saveLocalProtocols(userId, filtered);
    await createAuditLog(userId, protocolId, "DELETE", { name: { old: oldProtocol.name, new: null } }, "Protocol deleted.");
    return;
  }

  try {
    await deleteDoc(doc(db, 'protocols', protocolId));
    await createAuditLog(userId, protocolId, "DELETE", { name: { old: oldProtocol.name, new: null } }, "Protocol deleted.");
  } catch (e) {
    throw new Error(`Failed to delete protocol: ${e.message}`);
  }
}

export async function createAuditLog(userId, protocolId, actionType, changedFields, notes = "") {
  const now = new Date().toISOString();
  const logItem = {
    userId,
    protocolId,
    actionType,
    changedFields,
    notes,
    timestamp: now
  };

  if (isLocalMode(userId)) {
    const localLogs = getLocalAuditLogs(userId);
    localLogs.unshift({ id: 'a_' + Date.now(), ...logItem });
    saveLocalAuditLogs(userId, localLogs);
    return;
  }

  try {
    await addDoc(collection(db, 'audit_logs'), {
      ...logItem,
      timestamp: serverTimestamp()
    });
  } catch (e) {
    throw new Error(`Failed to create audit log: ${e.message}`);
  }
}

export async function getAuditLogs(userId, protocolId = null) {
  if (isLocalMode(userId)) {
    const logs = getLocalAuditLogs(userId);
    return protocolId ? logs.filter(l => l.protocolId === protocolId) : logs;
  }

  try {
    let q;
    if (protocolId) {
      q = query(
        collection(db, 'audit_logs'),
        where('userId', '==', userId),
        where('protocolId', '==', protocolId)
      );
    } else {
      q = query(
        collection(db, 'audit_logs'),
        where('userId', '==', userId)
      );
    }

    const querySnapshot = await getDocs(q);
    const logs = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // handle firestore timestamp to string
      const ts = data.timestamp?.toDate ? data.timestamp.toDate().toISOString() : (data.timestamp || new Date().toISOString());
      logs.push({ id: doc.id, ...data, timestamp: ts });
    });
    logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return logs;
  } catch (e) {
    throw new Error(`Failed to load audit logs: ${e.message}`);
  }
}

export async function seedDefaultStack(userId) {
  const created = [];
  for (const item of SEED_PROTOCOLS) {
    const newDoc = await addProtocol(userId, item);
    created.push(newDoc);
  }
  return created;
}

// Checkpoint & Snapshot Management
export async function createCheckpoint(userId, protocols, reason = "Manual Checkpoint") {
  const now = new Date().toISOString();
  const cleanProtocols = protocols.map(p => {
    const { id, ...rest } = p;
    return rest;
  });

  const payload = {
    userId,
    reason,
    protocolCount: cleanProtocols.length,
    protocols: cleanProtocols,
    timestamp: now
  };

  if (isLocalMode(userId)) {
    const checkpointId = 'cp_' + Date.now();
    const newCheckpoint = { id: checkpointId, ...payload };
    const local = getLocalCheckpoints(userId);
    local.unshift(newCheckpoint);
    saveLocalCheckpoints(userId, local);
    await createAuditLog(
      userId, 
      'all', 
      "CREATE", 
      {}, 
      `Created protocol snapshot checkpoint: "${reason}" (${cleanProtocols.length} protocols saved)`
    );
    return newCheckpoint;
  }

  try {
    const docRef = await addDoc(collection(db, 'protocol_checkpoints'), {
      ...payload,
      createdAt: serverTimestamp()
    });
    await createAuditLog(
      userId, 
      'all', 
      "CREATE", 
      {}, 
      `Created protocol snapshot checkpoint: "${reason}" (${cleanProtocols.length} protocols saved)`
    );
    return { id: docRef.id, ...payload };
  } catch (e) {
    throw new Error(`Failed to create checkpoint: ${e.message}`);
  }
}

export async function getCheckpoints(userId) {
  if (isLocalMode(userId)) {
    const local = getLocalCheckpoints(userId);
    return local.filter(cp => cp.userId === userId || !cp.userId);
  }

  try {
    const q = query(
      collection(db, 'protocol_checkpoints'),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const checkpoints = [];
    const seen = new Set();
    querySnapshot.forEach((doc) => {
      if (!seen.has(doc.id)) {
        seen.add(doc.id);
        const data = doc.data();
        const ts = data.createdAt?.toDate 
          ? data.createdAt.toDate().toISOString() 
          : (data.timestamp || new Date().toISOString());
        checkpoints.push({ id: doc.id, ...data, timestamp: ts });
      }
    });
    checkpoints.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return checkpoints;
  } catch (e) {
    throw new Error(`Failed to load checkpoints: ${e.message}`);
  }
}

export async function deleteCheckpoint(userId, checkpointId) {
  if (isLocalMode(userId)) {
    const local = getLocalCheckpoints(userId);
    const filtered = local.filter(cp => cp.id !== checkpointId);
    saveLocalCheckpoints(userId, filtered);
    return;
  }

  try {
    await deleteDoc(doc(db, 'protocol_checkpoints', checkpointId));
  } catch (e) {
    throw new Error(`Failed to delete checkpoint: ${e.message}`);
  }
}

export async function clearAllProtocols(userId, currentProtocols) {
  // 1. Create safety recovery checkpoint first
  let checkpoint = null;
  if (currentProtocols && currentProtocols.length > 0) {
    checkpoint = await createCheckpoint(
      userId, 
      currentProtocols, 
      `Pre-Clear Recovery Checkpoint (${currentProtocols.length} protocols)`
    );
  }

  if (isLocalMode(userId)) {
    saveLocalProtocols(userId, []);
    await createAuditLog(
      userId, 
      'all', 
      "DELETE", 
      { count: { old: currentProtocols.length, new: 0 } }, 
      `Cleared all protocols from stack. Recovery checkpoint created: ${checkpoint?.id || 'saved'}.`
    );
    return { success: true, checkpoint };
  }

  // 2. Delete from Firestore
  try {
    const q = query(collection(db, 'protocols'), where('userId', '==', userId));
    const snap = await getDocs(q);
    const deletePromises = [];
    snap.forEach((d) => {
      deletePromises.push(deleteDoc(doc(db, 'protocols', d.id)));
    });
    await Promise.all(deletePromises);
    await createAuditLog(
      userId, 
      'all', 
      "DELETE", 
      { count: { old: currentProtocols.length, new: 0 } }, 
      `Cleared all protocols from stack. Recovery checkpoint created: ${checkpoint?.id || 'saved'}.`
    );
    return { success: true, checkpoint };
  } catch (e) {
    throw new Error(`Failed to clear all protocols: ${e.message}`);
  }
}

export async function restoreFromCheckpoint(userId, checkpoint) {
  if (!checkpoint || !checkpoint.protocols) {
    throw new Error("Invalid checkpoint provided for restoration");
  }

  const restored = [];
  for (const item of checkpoint.protocols) {
    const created = await addProtocol(userId, item);
    restored.push(created);
  }

  // Log Audit record
  await createAuditLog(
    userId, 
    'all', 
    "CREATE", 
    {}, 
    `Restored ${restored.length} protocols from checkpoint "${checkpoint.reason}" (${checkpoint.timestamp})`
  );

  return restored;
}
